import router from '@/router/index.js';
import mixin from "@/mixin/index.js";
import functions from "@/router/functions.js";

//CONSTANTS
import labels from '@/constants/labels';
import messages from '@/constants/messages.js';
import parameters from '@/constants/parameters.js';

//PLUGINS
import { analyticsLogEvent, analyticsSetUserId } from "@/plugins/firebase.js";
import { openExternalUrl } from '@/platform/browser.js';
import { displaySupportChat, displayHelpCenter, displayHelpArticle, shutdownSupport } from "@/plugins/support.plugins.js";
import storage from '@/plugins/storage.plugins.js';

//SERVICES
import loginServices from '@/services/login.services.js';
import preferencesServices from '@/services/preferences.services.js';
import entitiesServices from '@/services/entities.services.js';
import partnersServices from '@/services/partners.services.js';
import clinicalDrafts from '@/services/clinical-drafts.js';

const root = { root: true };

const setBreadCrumbs = function (state, breadcrumbs) {
    state.breadcrumbs = breadcrumbs;
    window.sessionStorage.setItem('vetalfa-breadcrumbs', JSON.stringify(breadcrumbs));
}
const getBreadCrumbs = function () {
    return window.sessionStorage.getItem('vetalfa-breadcrumbs') ? JSON.parse(window.sessionStorage.getItem('vetalfa-breadcrumbs')) : [];
}
async function verifyEmailCodeQueryArguments(commit, dispatch) {
    if (router.app._route.query.email && router.app._route.query.code) {
        commit("setEmailConfirmation", false);
        await dispatch('general/confirmEmailCode', {
            email: router.app._route.query.email.trim(),
            code: router.app._route.query.code
        }, root);
    }
}
function displayPurchasesFromStripeSuccessAlert(commit) {
    if (router.app._route.query.purchases) {
        commit("setConfirmModel", {
            active: true,
            alert: true,
            text: messages.PURCHASE_SUCCESS,
            callback: () => { },
            confirmText: labels.CONTINUE,
        });
    }
}
//ACTIONS
export default {

    async updateApp({ state, dispatch, commit }) {
        let versionStatus = parameters.OK_APP_VERSION;
        await verifyEmailCodeQueryArguments(commit, dispatch);
        try {
            let response = await preferencesServices.getVersions(state.user);
            displayPurchasesFromStripeSuccessAlert(commit);
            if (state.user) {
                dispatch('preferences/getPreferencesByMode', { mode: 'searches' }, root);
            }
            versionStatus = response.status;
            state.synonyms = await storage.storageSelect('synonyms');
        } catch (err) {
            console.log(err);
        } finally {
            switch (versionStatus) {
                case parameters.OK_APP_VERSION:
                    if (router.app._route.name == "update") dispatch('general/routeRedirect', { name: 'home' }, root);
                    else commit("general/setLoadingOff", null, root);
                    break;
                case parameters.OUTDATED_APP_VERSION:
                    alert(messages.OUTDATED_APP_VERSION);
                    dispatch('general/routeRedirect', { name: 'home' }, root);
                    break;
                case parameters.FORBIDDEN_APP_VERSION:
                    alert(messages.FORBIDDEN_APP_VERSION);
                    break;
                default:
                    dispatch('general/routeRedirect', { name: 'home' }, root);
            }
        }
    },

    async getUserProvidersFromEmail({ dispatch }, email) {
        try {
            return await loginServices.getUserProvidersFromEmail(email);
        } catch (err) {
            dispatch("showError", messages.ERROR);
        }
    },

    async loginWithEmail({ state, dispatch }, { email, retry, password }) {
        let response = false;
        try {
            password = password ? mixin.methods._hash(password) : null;
            dispatch("general/logEvent", { name: 'loginWithEmail', object: { 'id': email } }, root);
            await loginServices.loginWithEmail(email, retry, password);
            response = true;
            const detectOutsideLogin = (evt) => {
                if (evt.isTrusted && !!evt.newValue && !evt.oldValue && evt.key === 'vetalfa') {
                    window.location.reload();
                }
            };
            window.removeEventListener('storage', detectOutsideLogin)
            window.addEventListener('storage', detectOutsideLogin);
        } catch {
            dispatch("showError", state.user ? messages.EMAIL_CONFIRMATION_ERROR : messages.ERROR);
        }
        return response;
    },

    async confirmEmailCode({ commit, state, dispatch }, { code, email }) {
        dispatch("general/logEvent", { name: 'confirmEmailCode', object: { 'id': email } }, root);
        try {
            const user = await loginServices.confirmEmailCode(code, email);
            state.user = user;
            dispatch("general/logEvent", { name: 'confirmEmailCodeSuccess', object: { 'id': email } }, root);
            const needsNationalityOrCPF = !user.nationality && !user.cpf;
            if (!user.displayName || !user.questions || needsNationalityOrCPF || !user.birthday || !user.questions) {
                dispatch('openRegistration');
                return true;
            }
            storage.storageUpsert("user", null, state.user, () => { }, () => { });
            analyticsSetUserId(state.user.uid);
            await dispatch("purchases/configurePurchases", null, root);
            dispatch("routeRedirect", { name: "update" });
            return true;
        } catch (e) {
            dispatch("general/logEvent", { name: 'confirmEmailCodeError', object: { 'id': email } }, root);
            if (e.message && e.message.includes('Network Error') && mixin.methods._isCordovaApp()) {
                dispatch("showAlert", { text: messages.ERROR_NETWORK });
            } else {
                dispatch("showError", messages.INVALID_EMAIL_CODE);
            }
            return false;
        }
    },

    async loginWithPassword({ commit, state, dispatch }, { password, email }) {
        dispatch("general/logEvent", { name: 'loginWithPassword', object: { 'id': email } }, root);
        try {
            password = !!password ? mixin.methods._hash(password) : null;
            const user = await loginServices.loginWithPassword(password, email);
            state.user = user;
            dispatch("general/logEvent", { name: 'loginWithPasswordSuccess', object: { 'id': email } }, root);
            const needsNationalityOrCPF = !user.nationality && !user.cpf;
            if (!user.displayName || !user.questions || needsNationalityOrCPF || !user.birthday || !user.questions) {
                dispatch('openRegistration');
                return true;
            }
            storage.storageUpsert("user", null, state.user, () => { }, () => { });
            analyticsSetUserId(state.user.uid);
            await dispatch("purchases/configurePurchases", null, root);
            dispatch("routeRedirect", { name: "update" });
            return true;
        } catch (e) {
            dispatch("general/logEvent", { name: 'loginWithPasswordError', object: { 'id': email } }, root);
            if (e.message && e.message.includes('Network Error') && mixin.methods._isCordovaApp()) {
                dispatch("showAlert", { text: messages.ERROR_NETWORK });
            } else {
                dispatch("showError", messages.INVALID_EMAIL_PASSWORD);
            }
            return false;
        }
    },

    async initializeApp({ commit, state, dispatch }) {
        const { user } = state;
        if (!user.email || !user.uid || user.email.includes('privaterelay.appleid') || !user.displayName) {
            return dispatch('completeLogout');
        }
        await dispatch('preferences/hydrateHomeLocalData', null, root);
        dispatch('preferences/getPreferencesByMode', { mode: 'searches' }, root);
        const halfHourMiliseconds = 1800000;
        const now = new Date().getTime();
        const hasActivity = state.postActivity == null || (now - state.postActivity) > halfHourMiliseconds;
        if (hasActivity) {
            dispatch("general/logEvent", mixin.methods._getPlatform(), root);
            dispatch("general/logEvent", { name: 'postActivity', object: { 'id': state.user.uid } }, root);
            state.postActivity = now;
            loginServices.postActivity(state.user.uid);
        }
    },

    openRegistration({ dispatch, state }) {
        dispatch("general/logEvent", { name: 'openRegistration', object: { 'id': state.user.uid } }, root);
        dispatch("routeRedirect", { name: 'registration' });
    },

    openEmailDialog({ commit }) {
        commit("setEmailConfirmation", true);
    },

    logEvent(_store, evt) {
        if (process.env.NODE_ENV == 'production') {
            if (typeof evt == 'string') {
                analyticsLogEvent(evt);
            } else {
                analyticsLogEvent(evt.name, evt.object);
            }
        }
    },

    openExternalUrl({ dispatch }, { url, inAppBrowser, system }) {
        if (url.includes('http://')) {
            url = url.replace('http', 'https');
        }
        if (!url.includes('https')) {
            url = 'https://' + url;
        }
        dispatch("logEvent", { name: 'open_external_url', object: { 'url': url } });

        const useInAppBrowser = mixin.methods._isCordovaApp() &&
            url != parameters.INSTAGRAM_LINK &&
            url != parameters.APP_ON_PLAY_STORE &&
            inAppBrowser;
        openExternalUrl(url, { inAppBrowser: useInAppBrowser, system });
    },

    openWhatsapp({ dispatch }, { phone, text }) {
        const { _isMobile, _isCordovaApp } = mixin.methods;
        const wppLink = `https://wa.me/55${phone}?text=${text}`;
        dispatch("general/openExternalUrl", {
            url: wppLink,
            inAppBrowser: true,
            system: true
        }, root);
    },

    contactUs({ dispatch, commit, state }) {
        commit("setLoadingOn");
        dispatch("logEvent", 'contact_us');
        displaySupportChat();
        setTimeout(() => { commit("setLoadingOff"); }, 1000);
    },

    displayHelpCenter({ dispatch, commit }) {
        commit("setLoadingOn");
        dispatch("logEvent", 'displayHelpCenter');
        displayHelpCenter();
        setTimeout(() => { commit("setLoadingOff"); }, 1000);
    },

    displayHelpArticle({ dispatch, commit }, { id }) {
        commit("setLoadingOn");
        dispatch("logEvent", 'displayHelpArticle');
        displayHelpArticle(id);
        setTimeout(() => { commit("setLoadingOff"); }, 1000);
    },

    routeBack: async ({ state }) => {
        const hashPart = (window.location.href.split('#')[1] || '').split('?')[0].replace('%E2%80%93', '');
        if (router.history.current.path.replace('–', '') != hashPart || window.history.length < 3) {
            state.breadcrumb = [];
            router.replace('/').then(_ => {
                window.location.reload();
            });
        } else if (state.routeCount > 1) {
            state.routeCount -= 2;
            router.back();
        } else {
            state.routeCount = 0;
            router.replace('/');
        }
    },

    routeRedirect({ state }, { name, query, fromOption }) {
        if (fromOption) {
            name = state.from.name;
            query = state.from.query;
            state.from = null;
        }
        router.push({ name, query });
    },

    routePathRedirect({ commit }, { path }) {
        router.push({ path: path.replace("%", "percentage") || "/home" });
    },

    showAlert({ commit }, { text, color, icon }) {
        commit("setAlert", {
            active: true,
            color: color || 'primary',
            text: text,
            icon: icon
        });
    },

    showMessage({ commit }, text) {
        commit("setAlert", {
            active: true,
            color: "success",
            text: text || messages.SUCCESS
        });
    },

    async loadingHandler({ commit, dispatch }, { action, logEventArgs, errorAction = null, showMessage = false }) {
        try {
            commit("general/setLoadingOn", null, root);
            dispatch("general/logEvent", logEventArgs, root);
            return await action();
        } catch (err) {
            showMessage = false;
            // Axios errors may contain the complete clinical request body/token.
            // Keep diagnostics useful without logging those payloads.
            console.warn('Solicitação não concluída', err && err.response ? err.response.status : 'network');
            if (errorAction) return await errorAction(err);
            dispatch("general/showError", null, root);
        } finally {
            if (showMessage) {
                dispatch("general/showMessage", null, root);
            }
            commit("general/setLoadingOff", null, root);
        }
    },

    showError({ commit, dispatch }, args) {
        let error = null;
        let color = 'error';
        let message = args;
        if (args && typeof args !== 'string') {
            if (args['event']) {
                dispatch('logEvent', `showError_${args['event']}`);
            }
            if (args['back']) {
                dispatch('routeBack');
            }
            message = args['message'];
            error = args['error'];
            console.log(error);
            color = args['color'];
        }
        if (!!error && !!error.message && error.message.includes('Network Error')) {
            message = messages.ERROR_NETWORK;
            color = "primary";
        }
        commit("setAlert", {
            active: true,
            color,
            text: message || messages.ERROR
        });
    },

    async copyToClipboard({ dispatch }, text) {
        const { _isMobile, _getMessage } = mixin.methods;
        if (navigator.share && _isMobile()) {
            navigator.share({
                title: _getMessage("SHARE_TITLE"),
                url: text
            });
        } else {
            await navigator.clipboard.writeText(text);
            dispatch("showMessage", messages.COPIED_TO_CLIPBOARD);
        }
    },

    async confirmLogout({ commit }) {
        commit("setConfirmModel", {
            active: true,
            text: messages.CONFIRM_LOGOUT,
            action: "general/completeLogout",
            confirmText: labels.CONFIRM_LOGOUT,
            closeText: labels.CANCEL,
            parameters: null
        });
    },

    async completeLogout({ commit, dispatch, state }) {
        // Stop the active editor before waiting for and removing this user's
        // pending local writes. Drafts belonging to other users are untouched.
        window.dispatchEvent(new CustomEvent('vetalfa:logout'));
        try {
            if (state.user && state.user.uid) await clinicalDrafts.clearUser(state.user.uid);
        } catch {
            window.dispatchEvent(new CustomEvent('vetalfa:logout-cancelled'));
            console.error('Unable to clear clinical drafts on logout');
            dispatch("showError", messages.ERROR);
            return false;
        }
        dispatch("logEvent", 'logout');
        shutdownSupport();
        storage.storageUpsert('configuration', 'recent_searches', []);
        dispatch("preferences/clearHomeLocalData", null, root);
        dispatch("patients/clearPatientsAndOwners", null, root);
        await storage
            .storageDeleteWithId("user", null,
                _success => {
                    commit("setUser", null);
                    dispatch("routeRedirect", { name: 'login' });
                }, err => {
                    console.log(err);
                    dispatch("showError", messages.ERROR);
                });
        return true;
    },

    async saveUserData({ commit, dispatch, state, rootGetters }, { user = {}, hasRedirect = false, successCallback = null, field }) {
        dispatch("logEvent", 'saveUserData');
        commit("setLoadingOn");
        const models = rootGetters["algorithms/getModels"];
        const inputs = rootGetters["algorithms/inputs"];
        const profileOnly = field === 'mapaRegistrationNumber';
        const requestedMapRegistration = user.mapaRegistrationNumber;
        user = {
            ...user,
            uid: state.user.uid,
            displayName: models["displayName"] || undefined,
            displayNamePrefix: field === 'displayNamePrefix' ? (models["displayNamePrefix"] || "") : (models["displayNamePrefix"] || undefined),
            birthday: models["birthday"] || undefined,
            cpf:
                models["cpf"]
                    ? (models["cpf"] || "").replace(/[.-]/g, "")
                    : undefined,
            nationality:
                inputs[0] == 0 ? undefined : models["nationality"],
            crmvNumber: models["crmvNumber"] || undefined,
            crmvState: models["crmvState"] || undefined,
        }
        if (profileOnly) user = { uid: state.user.uid, mapaRegistrationNumber: requestedMapRegistration };
        if (!profileOnly && models['addressZipCode']) {
            user['address'] = {
                zipCode: models["addressZipCode"] || undefined,
                street: models["addressStreet"] || undefined,
                number: models["addressNumber"] || undefined,
                complement: field === 'address' ? (models["addressComplement"] || "") : (models["addressComplement"] || undefined),
                neighborhood: models["addressNeighborhood"] || undefined,
                city: models["addressCity"] || undefined,
                state: models["addressState"] || undefined,
            }
        }
        try {
            await loginServices.saveUserData(user);
            if (hasRedirect) {
                await dispatch("saveUserOnApp", { user: { ...state.user, ...user }, hasRedirect });
            } else {
                await dispatch("refreshAccessToken");
                await dispatch("showMessage", messages.REGISTRATION_UPDATE_SUCCESS);
                if (!successCallback) {
                    dispatch("routeBack");
                } else {
                    successCallback();
                }
            }
            return { ok: true };
        } catch (error) {
            dispatch("showError", messages.ERROR);
            return { ok: false, error };
        } finally {
            commit("setLoadingOff");
            commit("setVeterinaryColleges", []);
        }
    },

    async getVeterinaryColleges({ commit, dispatch }) {
        commit("setLoadingOn", true);
        try {
            const payload =
                await entitiesServices.getItem("configuration", 'colleges');
            commit("setVeterinaryColleges", payload.colleges);
        } catch (err) {
            console.log(err);
            dispatch("showError", messages.ERROR);
            commit("setVeterinaryColleges", []);
        } finally {
            commit("setLoadingOff", true);
        }
    },

    saveUserOnApp({ dispatch, commit }, { user, hasRedirect }) {
        commit("setUser", user);
        storage.storageUpsert("user", null, user,
            function () {
                commit("setUser", user);
                if (hasRedirect) {
                    dispatch("routeRedirect", { name: "update" });
                }
            },
            function (err) {
                console.log(err);
                dispatch("showError", messages.ERROR_SAVE_USER);
                commit("setUser", null);
                if (hasRedirect) {
                    dispatch("routeRedirect", { name: "update" });
                }
            });
    },

    refreshAccessToken: async ({ commit }) => {
        try {
            commit("setLoadingOn");
            await loginServices.getToken();
        } catch (e) {
            throw e;
        } finally {
            commit("setLoadingOff");
        }

    },

    deleteUserConfirmation: async ({ dispatch, commit, state }) => {
        commit("setLoadingOn", true);
        try {
            await loginServices.deleteUserConfirmation(state.user.uid);
            dispatch("completeLogout");
        } catch (err) {
            dispatch("showError", messages.ERROR);
        } finally {
            commit("setLoadingOff", true);
        }
    },

    partnersRedirect: async ({ dispatch, commit, state }) => {
        try {
            function getParameterByName(name, url = window.location.href) {
                name = name.replace(/[\[\]]/g, '\\$&');
                let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
                    results = regex.exec(url);
                if (!results) return null;
                if (!results[2]) return '';
                return decodeURIComponent(results[2].replace(/\+/g, ' '));
            }
            let token = getParameterByName('token') || window.sessionStorage.getItem('vetalfa_tkn');
            await commit("setLoadingOn");
            if (!token) {
                throw new Error();
            }
            let payload = await partnersServices.partnersValidateToken(token);

            analyticsSetUserId(payload['organization_id']);
            await commit("setUser", {
                displayName: payload['organization_name'],
                uid: payload['organization_id'],
                email: payload['user_email'] || 'partners@vetalfa.com',
                providerId: 'partners',
                questions: {}
            });
            window.sessionStorage.setItem('vetalfa_tkn', token);
            let search_query = getParameterByName('search_query');
            if (search_query) {
                setTimeout(() => {
                    dispatch('routePathRedirect', {
                        path: `/items/searches?search_query=${search_query}`
                    });
                }, 100);
            }
            return true;
        } catch (err) {
            window.sessionStorage.removeItem('vetalfa_tkn');
            await commit("setConfirmModel", {
                active: true,
                alert: true,
                text: messages.PARTNERS_SESSION_NOT_ALLOWED,
                callback: () => { window.location.assign(parameters.HOME_URL); },
                confirmText: labels.CONTINUE,
            });
            return false;
        } finally {
            await commit("setLoadingOff");
        }
    },
    getBreadCrumbs,
    pushOrPopRouteOfBreadcrumb: async ({ state }, { to, offset }) => {
        let breadcrumbs = getBreadCrumbs();
        if (!!offset) {
            breadcrumbs.splice(-offset);
            return setBreadCrumbs(state, breadcrumbs);
        }
        if (!breadcrumbs.length) {
            breadcrumbs = [{
                name: 'Início',
                path: '/home',
                disabled: false
            }];
        }
        if (!!breadcrumbs[breadcrumbs.length - 2] && breadcrumbs[breadcrumbs.length - 2].path === to.path) {
            breadcrumbs.pop();
            return setBreadCrumbs(state, breadcrumbs);
        }
        if (to && to.meta && !to.meta.publicPage && !to.meta.avoidBreadcrumb && breadcrumbs && breadcrumbs.length && breadcrumbs[breadcrumbs.length - 1].path !== to.path) {
            breadcrumbs.push({
                name: to.meta.ptName,
                path: to.path,
                disabled: false
            });
        }
        if (to.path === '/') {
            breadcrumbs = [];
        }
        return setBreadCrumbs(state, breadcrumbs);
    },
    popLastCrumbItem: async ({ state }, title) => {
        let breadcrumbs = getBreadCrumbs();
        breadcrumbs.pop();
        setBreadCrumbs(state, breadcrumbs);
    },
    setLastCrumbItemTitle: async ({ state }, title) => {
        let breadcrumbs = getBreadCrumbs();
        breadcrumbs[breadcrumbs.length - 1].name = title;
        setBreadCrumbs(state, breadcrumbs);
    },

    openNews({ dispatch }) {
        dispatch("displayHelpCenter");
    }

}
